import { useMemo, useState } from "react";
import { ChevronLeft, Plus, Upload } from "lucide-react";
import { T, inputStyle } from "../lib/theme";
import { fmtDate } from "../lib/dates";
import type { Meeting, MemberSummary, SpeakerType } from "../lib/types";
import { historyFor, type MemberInput } from "../hooks/useData";
import { Card, EmptyState, FabAdd, SearchField, Sheet, SheetHeader, VirtualList, YouthTag } from "../components/ui";
import { ROW_H } from "../components/PersonRow";
import { HistorySheet } from "../components/HistorySheet";

export function Members({
  members,
  meetings,
  saving,
  onBack,
  onSave,
  onImport,
}: {
  members: MemberSummary[];
  meetings: Meeting[];
  saving: boolean;
  onBack: () => void;
  onSave: (m: MemberInput) => void;
  onImport: (rows: Array<Omit<MemberInput, "id">>) => void;
}) {
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState<string | null>(null);
  const [edit, setEdit] = useState<MemberSummary | null>(null);
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);
  const [showMoved, setShowMoved] = useState(false);

  const list = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return members
      .filter((m) => (showMoved ? true : m.status === "active"))
      .filter(
        (m) =>
          !needle ||
          `${m.last_name} ${m.first_name}`.toLowerCase().includes(needle) ||
          `${m.first_name} ${m.last_name}`.toLowerCase().includes(needle),
      )
      .sort((a, b) => a.last_name.localeCompare(b.last_name) || a.first_name.localeCompare(b.first_name));
  }, [members, q, showMoved]);

  const activeCount = members.filter((m) => m.status === "active").length;
  const detailMember = detail ? members.find((m) => m.id === detail) : undefined;

  return (
    <>
      <div style={{ background: T.navy, color: "#fff", padding: "14px 18px 16px" }}>
        <button
          onClick={onBack}
          style={{ color: "rgba(255,255,255,.7)", display: "flex", alignItems: "center", gap: 4, fontSize: 13.5, marginBottom: 8 }}
        >
          <ChevronLeft size={17} /> Up Next
        </button>
        <h1 className="serif" style={{ margin: 0, fontSize: 24, fontWeight: 500 }}>
          Ward members
        </h1>
        <div style={{ fontSize: 12.5, color: "rgba(255,255,255,.55)", marginTop: 3 }}>
          {activeCount} active · {members.length - activeCount} moved out
        </div>

        <button
          onClick={() => setAdding(true)}
          style={{
            width: "100%",
            marginTop: 14,
            padding: "12px 0",
            borderRadius: 10,
            background: "#fff",
            color: T.navy,
            fontSize: 14.5,
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 7,
          }}
        >
          <Plus size={18} strokeWidth={2.4} /> Add a member
        </button>

        <div style={{ marginTop: 10 }}>
          <SearchField value={q} onChange={setQ} placeholder="Search by last name" />
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 18px", background: T.paper }}>
        <button onClick={() => setShowMoved((x) => !x)} style={{ fontSize: 12.5, color: T.accent, fontWeight: 600 }}>
          {showMoved ? "Hide moved out" : "Show moved out"}
        </button>
        <button
          onClick={() => setImporting(true)}
          style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12.5, color: T.accent, fontWeight: 600 }}
        >
          <Upload size={14} /> Import
        </button>
      </div>

      <div style={{ background: "#fff" }}>
        {list.length === 0 && <EmptyState>No members match that search.</EmptyState>}
        <VirtualList
          items={list}
          rowHeight={ROW_H}
          renderRow={(m) => (
            <button
              key={m.id}
              onClick={() => setDetail(m.id)}
              className="row-press"
              style={{
                width: "100%",
                textAlign: "left",
                height: ROW_H,
                padding: "0 18px",
                borderBottom: `1px solid ${T.line}`,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 12,
                opacity: m.status === "moved_out" ? 0.5 : 1,
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div
                  className="serif"
                  style={{ fontSize: 16.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                >
                  {m.last_name}, {m.first_name}
                  {m.speaker_type === "youth" && <YouthTag />}
                </div>
                <div style={{ fontSize: 11, color: T.faint, marginTop: 1 }}>
                  {m.status === "moved_out" ? "Moved out · " : ""}
                  {m.speak_count} talk{m.speak_count === 1 ? "" : "s"}, {m.prayer_count} prayer
                  {m.prayer_count === 1 ? "" : "s"}
                </div>
              </div>

              <div style={{ textAlign: "right", flexShrink: 0 }}>
                {m.last_spoke_date ? (
                  <>
                    <div
                      style={{ fontSize: 9, fontWeight: 600, letterSpacing: ".1em", textTransform: "uppercase", color: T.faint }}
                    >
                      Last spoke
                    </div>
                    <div className="tnum" style={{ fontSize: 12.5, color: T.muted, whiteSpace: "nowrap", marginTop: 1 }}>
                      {fmtDate(m.last_spoke_date)}
                    </div>
                  </>
                ) : (
                  <div style={{ fontSize: 11.5, color: T.brass, fontWeight: 600, whiteSpace: "nowrap" }}>never spoken</div>
                )}
              </div>
            </button>
          )}
        />
      </div>

      <FabAdd onClick={() => setAdding(true)} label="Add a member" />

      {detailMember && (
        <HistorySheet
          m={detailMember}
          history={historyFor(detailMember.id, meetings)}
          onClose={() => setDetail(null)}
          onEdit={() => {
            setEdit(detailMember);
            setDetail(null);
          }}
        />
      )}

      {(edit || adding) && (
        <MemberSheet
          member={edit}
          saving={saving}
          onClose={() => {
            setEdit(null);
            setAdding(false);
          }}
          onSave={(m) => {
            onSave(m);
            setEdit(null);
            setAdding(false);
          }}
        />
      )}

      {importing && (
        <ImportSheet
          saving={saving}
          onClose={() => setImporting(false)}
          onImport={(rows) => {
            onImport(rows);
            setImporting(false);
          }}
        />
      )}
    </>
  );
}

function MemberSheet({
  member,
  saving,
  onClose,
  onSave,
}: {
  member: MemberSummary | null;
  saving: boolean;
  onClose: () => void;
  onSave: (m: MemberInput) => void;
}) {
  const [first, setFirst] = useState(member?.first_name ?? "");
  const [last, setLast] = useState(member?.last_name ?? "");
  const [type, setType] = useState<SpeakerType>(member?.speaker_type ?? "adult");
  const [status, setStatus] = useState<"active" | "moved_out">(member?.status ?? "active");

  const valid = first.trim().length > 0 && last.trim().length > 0;

  return (
    <Sheet onClose={onClose}>
      <div style={{ padding: 20 }}>
        <SheetHeader title={member ? "Edit member" : "Add member"} onClose={onClose} />

        <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
          <div style={{ flex: 1 }}>
            <div className="eyebrow" style={{ marginBottom: 6 }}>
              Last name
            </div>
            <input value={last} onChange={(e) => setLast(e.target.value)} style={inputStyle} />
          </div>
          <div style={{ flex: 1 }}>
            <div className="eyebrow" style={{ marginBottom: 6 }}>
              First name
            </div>
            <input value={first} onChange={(e) => setFirst(e.target.value)} style={inputStyle} />
          </div>
        </div>

        <div className="eyebrow" style={{ marginBottom: 7 }}>
          Speaks as
        </div>
        <div style={{ display: "flex", gap: 8, marginBottom: 18 }}>
          {(["adult", "youth"] as SpeakerType[]).map((v) => (
            <button
              key={v}
              onClick={() => setType(v)}
              style={{
                flex: 1,
                padding: 11,
                borderRadius: 9,
                fontSize: 14,
                fontWeight: 500,
                border: `1px solid ${type === v ? T.navy : T.line}`,
                background: type === v ? T.navy : "#fff",
                color: type === v ? "#fff" : T.ink,
              }}
            >
              {v === "adult" ? "Adult" : "Youth"}
            </button>
          ))}
        </div>

        {member && (
          <>
            <div className="eyebrow" style={{ marginBottom: 7 }}>
              Status
            </div>
            <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
              {(["active", "moved_out"] as const).map((v) => (
                <button
                  key={v}
                  onClick={() => setStatus(v)}
                  style={{
                    flex: 1,
                    padding: 11,
                    borderRadius: 9,
                    fontSize: 14,
                    fontWeight: 500,
                    border: `1px solid ${status === v ? T.navy : T.line}`,
                    background: status === v ? T.navy : "#fff",
                    color: status === v ? "#fff" : T.ink,
                  }}
                >
                  {v === "active" ? "In the ward" : "Moved out"}
                </button>
              ))}
            </div>
            <div style={{ fontSize: 12, color: T.muted, marginBottom: 18, lineHeight: 1.45 }}>
              Moving someone out hides them from Up Next and keeps everything they've spoken on.
            </div>
          </>
        )}

        <button
          disabled={!valid || saving}
          onClick={() =>
            onSave({
              id: member?.id,
              first_name: first.trim(),
              last_name: last.trim(),
              speaker_type: type,
              status: member ? status : undefined,
            })
          }
          style={{
            width: "100%",
            padding: 14,
            borderRadius: 11,
            fontSize: 15,
            fontWeight: 600,
            background: valid && !saving ? T.navy : T.line,
            color: valid && !saving ? "#fff" : T.faint,
            cursor: valid && !saving ? "pointer" : "default",
            marginBottom: 12,
          }}
        >
          {saving ? "Saving…" : member ? "Save changes" : "Add member"}
        </button>
      </div>
    </Sheet>
  );
}

/** One-time bulk load. Paste "Last, First" rows straight out of an LCR export. */
function ImportSheet({
  saving,
  onClose,
  onImport,
}: {
  saving: boolean;
  onClose: () => void;
  onImport: (rows: Array<Omit<MemberInput, "id">>) => void;
}) {
  const [text, setText] = useState("");
  const [youthMark, setYouthMark] = useState(true);

  const parsed = useMemo(() => {
    const rows: Array<Omit<MemberInput, "id">> = [];
    const bad: string[] = [];
    for (const raw of text.split("\n")) {
      const line = raw.trim();
      if (!line) continue;
      // "Last, First" or "Last, First, youth"
      const parts = line.split(",").map((p) => p.trim());
      if (parts.length < 2 || !parts[0] || !parts[1]) {
        bad.push(line);
        continue;
      }
      const isYouth = youthMark && parts[2]?.toLowerCase().startsWith("y");
      rows.push({ last_name: parts[0], first_name: parts[1], speaker_type: isYouth ? "youth" : "adult" });
    }
    return { rows, bad };
  }, [text, youthMark]);

  return (
    <Sheet onClose={onClose}>
      <div style={{ padding: 20 }}>
        <SheetHeader title="Import members" onClose={onClose} />

        <div style={{ fontSize: 13, color: T.muted, lineHeight: 1.55, marginBottom: 14 }}>
          One person per line as <strong>Last, First</strong>. Add a third column of{" "}
          <strong>youth</strong> to mark youth speakers. Everyone else is imported as an adult.
        </div>

        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={8}
          placeholder={"Ashworth, David\nAshworth, Rebecca\nAshworth, Samuel, youth"}
          style={{ ...inputStyle, fontFamily: "ui-monospace, monospace", fontSize: 13, lineHeight: 1.5 }}
        />

        <label style={{ display: "flex", alignItems: "center", gap: 8, margin: "12px 0", fontSize: 13, color: T.ink }}>
          <input type="checkbox" checked={youthMark} onChange={(e) => setYouthMark(e.target.checked)} />
          Treat a third column of "youth" as a youth speaker
        </label>

        {text.trim() && (
          <Card>
            <div style={{ fontSize: 13, color: T.ink }}>
              {parsed.rows.length} member{parsed.rows.length === 1 ? "" : "s"} ready to import
            </div>
            {parsed.bad.length > 0 && (
              <div style={{ fontSize: 12, color: T.brass, marginTop: 6, lineHeight: 1.5 }}>
                {parsed.bad.length} line{parsed.bad.length === 1 ? "" : "s"} will be skipped — they don't have a
                comma separating last and first name.
              </div>
            )}
          </Card>
        )}

        <button
          disabled={parsed.rows.length === 0 || saving}
          onClick={() => onImport(parsed.rows)}
          style={{
            width: "100%",
            padding: 14,
            borderRadius: 11,
            fontSize: 15,
            fontWeight: 600,
            background: parsed.rows.length && !saving ? T.navy : T.line,
            color: parsed.rows.length && !saving ? "#fff" : T.faint,
            marginBottom: 12,
          }}
        >
          {saving ? "Importing…" : `Import ${parsed.rows.length || ""} member${parsed.rows.length === 1 ? "" : "s"}`}
        </button>
      </div>
    </Sheet>
  );
}
